// B1.4: move-only from client.ts.
import { ApiClientCore, ApiError, buildQueryString } from './http/core';
import { buildAuthorizedFileUrlWithBase, buildChatAttachmentPreviewUrl as buildChatAttachmentPreviewUrlHelper } from './urls';

export class ChatApi extends ApiClientCore {
  async addRoomMember(roomId: number, data: { user_id: number; role?: string }): Promise<any> {
    return this.post(`/api/v1/chat/rooms/${roomId}/members`, data);
  }
  async archiveRoom(roomId: number, archived: boolean): Promise<any> {
    return this.put(`/api/v1/chat/rooms/${roomId}/archive?archived=${archived}`);
  }
  async createChatRoom(data: {
    type: string;
    name?: string;
    description?: string;
    audience_type?: 'manual' | 'organization' | 'location' | 'multi_location' | 'hybrid';
    member_ids?: number[];
    employee_member_ids?: number[];
    location_id?: number;
    location_ids?: number[];
    include_admins_managers?: boolean;
    is_dynamic_membership?: boolean;
    posting_policy?: 'everyone' | 'admins_only';
    forum_enabled?: boolean;
    initial_topic_titles?: string[];
  }): Promise<any> {
    return this.post('/api/v1/chat/rooms', data);
  }
  async createChatRoomTopic(roomId: number, body: Record<string, unknown>): Promise<any> {
    return this.post(`/api/v1/chat/rooms/${roomId}/topics`, body);
  }
  async deleteChatMessage(messageId: number): Promise<any> {
    return this.delete(`/api/v1/chat/messages/${messageId}`);
  }
  async deleteChatRoom(roomId: number): Promise<void> {
    await this.delete(`/api/v1/chat/rooms/${roomId}`);
  }
  async deleteChatRoomTopic(roomId: number, topicId: number): Promise<void> {
    await this.delete(`/api/v1/chat/rooms/${roomId}/topics/${topicId}`);
  }
  async editMessage(messageId: number, content: string): Promise<any> {
    return this.put(`/api/v1/chat/messages/${messageId}`, { content });
  }
  async forwardChatMessage(
    roomId: number,
    messageId: number,
    targetRoomId: number,
    targetTopicId?: number
  ): Promise<any> {
    const body: {
      message_id: number;
      target_room_id: number;
      target_topic_id?: number;
    } = { message_id: messageId, target_room_id: targetRoomId };
    if (targetTopicId != null) body.target_topic_id = targetTopicId;
    return this.post(`/api/v1/chat/rooms/${roomId}/messages/forward`, body);
  }
  async shareEntityToChat(
    roomId: number,
    entityType: 'task' | 'incident',
    entityId: number,
    topicId?: number
  ): Promise<any> {
    const body: { entity_type: 'task' | 'incident'; entity_id: number; topic_id?: number } = {
      entity_type: entityType,
      entity_id: entityId,
    };
    if (topicId != null) body.topic_id = topicId;
    return this.post(`/api/v1/chat/rooms/${roomId}/entity-cards`, body);
  }
  async getChatContacts(): Promise<{
    contacts: any[];
    employees_without_account_count: number;
  }> {
    return this.get('/api/v1/chat/contacts');
  }
  async getChatRoom(roomId: number): Promise<any> {
    return this.get(`/api/v1/chat/rooms/${roomId}`);
  }
  async getChatRoomTopics(roomId: number, q?: string): Promise<any[]> {
    const qs = q && q.trim() ? buildQueryString({ q: q.trim() }) : '';
    return this.get(`/api/v1/chat/rooms/${roomId}/topics${qs}`);
  }
  async getChatRooms(includeArchived = false): Promise<any> {
    return this.get(`/api/v1/chat/rooms${includeArchived ? '?include_archived=true' : ''}`);
  }
  async getPinnedMessage(roomId: number): Promise<any | null> {
    try {
      return await this.get(`/api/v1/chat/rooms/${roomId}/pin`);
    } catch {
      return null;
    }
  }
  async getRoomMedia(roomId: number, params?: { type?: string; limit?: number; before?: number }): Promise<any[]> {
    const { type, ...rest } = params || {};
    const query: Record<string, unknown> = { ...rest };
    if (type != null) {
      query.type_filter = type;
    }
    return this.get(`/api/v1/chat/rooms/${roomId}/media${buildQueryString(query)}`);
  }
  async getRoomMembers(roomId: number): Promise<any> {
    return this.get(`/api/v1/chat/rooms/${roomId}/members`);
  }
  async getRoomMessages(
    roomId: number,
    before?: number,
    limit = 50,
    threadRootId?: number,
    topicId?: number
  ): Promise<any> {
    const params: Record<string, any> = { limit };
    if (before !== undefined) params.before = before;
    if (threadRootId !== undefined) params.thread_root_id = threadRootId;
    if (topicId !== undefined) params.topic_id = topicId;
    return this.get(`/api/v1/chat/rooms/${roomId}/messages${buildQueryString(params)}`);
  }
  async hideMessageForMe(messageId: number): Promise<any> {
    return this.request(`/api/v1/chat/messages/${messageId}/hide`, { method: 'POST' });
  }
  async markRoomRead(roomId: number, lastMessageId: number, topicId?: number): Promise<{ ok: boolean }> {
    const body: { last_message_id: number; topic_id?: number } = { last_message_id: lastMessageId };
    if (topicId != null) body.topic_id = topicId;
    return this.put(`/api/v1/chat/rooms/${roomId}/read`, body);
  }
  async muteRoom(roomId: number, muted: boolean): Promise<any> {
    return this.put(`/api/v1/chat/rooms/${roomId}/mute?muted=${muted}`);
  }
  async patchChatRoomTopic(
    roomId: number,
    topicId: number,
    body: { clear_icon?: boolean; title?: string }
  ): Promise<any> {
    return this.patch(`/api/v1/chat/rooms/${roomId}/topics/${topicId}`, body);
  }
  buildAuthorizedFileUrl(url: string): string {
    return buildAuthorizedFileUrlWithBase(url, this.baseURL);
  }
  buildChatAttachmentPreviewUrl(fileUrl?: string | null, thumbnailUrl?: string | null): string {
    return buildChatAttachmentPreviewUrlHelper(fileUrl, thumbnailUrl);
  }
  async pinMessage(roomId: number, messageId: number | null): Promise<void> {
    await this.put(`/api/v1/chat/rooms/${roomId}/pin`, {
      message_id: messageId,
    });
  }
  async removeRoomMember(roomId: number, memberId: number): Promise<any> {
    return this.delete(`/api/v1/chat/rooms/${roomId}/members/${memberId}`);
  }
  async searchMessages(
    roomId: number,
    query: string,
    limit = 20,
    includeThread = false
  ): Promise<any[]> {
    return this.searchRoomMessages(roomId, query, { limit, includeThread });
  }
  async searchRoomMessages(
    roomId: number,
    q: string,
    params?: { limit?: number; includeThread?: boolean }
  ): Promise<any[]> {
    const query: Record<string, unknown> = { q, limit: params?.limit ?? 20 };
    if (params?.includeThread) query.include_thread = true;
    return this.get(`/api/v1/chat/rooms/${roomId}/messages/search${buildQueryString(query)}`);
  }
  async sendChatMessage(
    roomId: number,
    data: {
      content: string;
      message_type?: string;
      link_preview?: Record<string, unknown> | null;
      reply_to_id?: number;
      topic_id?: number;
    }
  ): Promise<any> {
    return this.post(`/api/v1/chat/rooms/${roomId}/messages`, data);
  }
  async sendMessage(
    roomId: number,
    content: string,
    replyToId?: number,
    linkPreview?: { url: string; title?: string; description?: string; image?: string },
    scheduledAt?: string,
    topicId?: number
  ): Promise<any> {
    const body: Record<string, unknown> = { content };
    if (replyToId != null) body.reply_to_id = replyToId;
    if (linkPreview) body.link_preview = linkPreview;
    if (scheduledAt) body.scheduled_at = scheduledAt;
    if (topicId != null) body.topic_id = topicId;
    return this.post(`/api/v1/chat/rooms/${roomId}/messages`, body);
  }
  async sendMessageWithAttachment(
    roomId: number,
    file: File,
    options?: { content?: string; replyToId?: number; topicId?: number; onProgress?: (n: number) => void }
  ): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);
    if (options?.content) formData.append('content', options.content);
    if (options?.replyToId != null) formData.append('reply_to_id', String(options.replyToId));
    if (options?.topicId != null) formData.append('topic_id', String(options.topicId));

    const url = `${this.baseURL}/api/v1/chat/rooms/${roomId}/messages/with-attachment`;
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', url);
      xhr.withCredentials = true;
      xhr.setRequestHeader('X-Client', 'web');
      if (options?.onProgress) {
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) options.onProgress?.(Math.round((e.loaded / e.total) * 100));
        };
      }
      xhr.onload = () => {
        let data: any = {};
        try {
          data = JSON.parse(xhr.responseText || '{}');
        } catch {
          /* noop */
        }
        if (xhr.status >= 200 && xhr.status < 300) resolve(data);
        else reject(new ApiError(xhr.status, data.detail || xhr.statusText, data));
      };
      xhr.onerror = () => reject(new ApiError(0, 'Network error', { network: true }));
      xhr.send(formData);
    });
  }
  async setChatRoomForum(roomId: number, enabled: boolean): Promise<any> {
    return this.put(`/api/v1/chat/rooms/${roomId}/forum`, { enabled });
  }
  async updateRoom(roomId: number, data: Record<string, unknown>): Promise<any> {
    return this.put(`/api/v1/chat/rooms/${roomId}`, data);
  }
  async uploadChatAttachment(
    roomId: number,
    messageId: number,
    file: File,
    options?: {
      onProgress?: (progress: number) => void;
      messageType?: 'voice' | 'video_note';
      duration?: number;
    }
  ): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);
    if (options?.messageType) formData.append('message_type', options.messageType);
    if (options?.duration != null) formData.append('duration', String(Math.round(options.duration)));

    // Cookie-based auth: XHR с withCredentials отправит __Host-ppc_access автоматически.
    const url = `${this.baseURL}/api/v1/chat/rooms/${roomId}/messages/${messageId}/attachments`;
    const send = (): Promise<any> =>
      new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', url);
        xhr.withCredentials = true;
        xhr.setRequestHeader('X-Client', 'web');

        if (options?.onProgress) {
          xhr.upload.onprogress = (event) => {
            if (!event.lengthComputable) return;
            options.onProgress?.(Math.round((event.loaded / event.total) * 100));
          };
        }

        xhr.onload = () => {
          const responseText = xhr.responseText || '{}';
          let data: any = {};
          try {
            data = JSON.parse(responseText);
          } catch {
            /* noop */
          }
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve(data);
            return;
          }
          reject(new ApiError(xhr.status, data.detail || xhr.statusText, data));
        };

        xhr.onerror = () => {
          reject(new ApiError(0, 'Network error', { network: true }));
        };

        xhr.send(formData);
      });

    try {
      return await send();
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        const refreshed = await this.refreshSession();
        if (refreshed) {
          return await send();
        }
        if (refreshed === null) this.handleAuthFailure('unauthorized');
      }
      throw err;
    }
  }
  async uploadRoomAvatar(roomId: number, file: File): Promise<{ avatar_url: string }> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await this.authedFetch(`${this.baseURL}/api/v1/chat/rooms/${roomId}/avatar`, {
      method: 'POST',
      body: formData,
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new ApiError(response.status, errorData.detail || response.statusText, errorData);
    }
    return response.json();
  }
}
